import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, Bed, Bath, Check, MapPin, Waves, ChevronLeft, ChevronRight, X, Send } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FanPage = () => {
  const { id_propiedad } = useParams();
  const [propiedad, setPropiedad] = useState(null);
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [preciosInfo, setPreciosInfo] = useState(null);
  
  // Galería de fotos
  const [fotoActual, setFotoActual] = useState(0);
  const [modalGaleriaAbierto, setModalGaleriaAbierto] = useState(false);
  
  // Selección de fechas en calendario
  const [fechaCheckIn, setFechaCheckIn] = useState(null);
  const [fechaCheckOut, setFechaCheckOut] = useState(null);
  const [cotizacion, setCotizacion] = useState(null);
  const [seleccionandoCheckOut, setSeleccionandoCheckOut] = useState(false);
  
  // Formulario de reserva
  const [modalReservaAbierto, setModalReservaAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [solicitudEnviada, setSolicitudEnviada] = useState(false);
  const [formReserva, setFormReserva] = useState({
    nombre_cliente: '',
    apellido_cliente: '',
    email_cliente: '',
    telefono_cliente: '',
    rut_cliente: '',
    direccion_cliente: '',
    fecha_check_in: '',
    fecha_check_out: '',
    num_personas: '',
    mensaje: ''
  });
  const [cedulaFile, setCedulaFile] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, [id_propiedad, year]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      let targetId = id_propiedad;
      
      if (!targetId) {
        const resList = await axios.get(`${API}/public/propiedades`);
        if (resList.data && resList.data.length > 0) {
          targetId = resList.data[0].id_propiedad;
        } else {
          toast.error('No hay propiedades configuradas');
          setLoading(false);
          return;
        }
      }

      const [resProp, resDisp, resPrecios] = await Promise.all([
        axios.get(`${API}/public/propiedad/${targetId}`),
        axios.get(`${API}/public/propiedad/${targetId}/disponibilidad?year=${year}`),
        axios.get(`${API}/public/propiedad/${targetId}/precios`)
      ]);
      
      if (!resProp.data.nombre?.toLowerCase().includes('morrillo')) {
        toast.error('Fan Page no disponible para esta propiedad');
        setLoading(false);
        return;
      }
      
      setPropiedad(resProp.data);
      setPreciosInfo(resPrecios.data);
      // Combinar reservas confirmadas y solicitudes pendientes
      const todasLasReservas = [
        ...(resDisp.data.reservas || []),
        ...(resDisp.data.solicitudes_pendientes || [])
      ];
      setDisponibilidad(todasLasReservas);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Propiedad no encontrada');
      setLoading(false);
    }
  };

  const enviarSolicitud = async (e) => {
    e.preventDefault();
    
    if (!formReserva.fecha_check_in || !formReserva.fecha_check_out) {
      toast.error('Selecciona las fechas de tu estadía');
      return;
    }

    setEnviando(true);
    try {
      let cedula_url = '';
      
      // Subir archivo de cédula si existe
      if (cedulaFile) {
        const formData = new FormData();
        formData.append('file', cedulaFile);
        
        const uploadRes = await axios.post(`${API}/upload-cedula`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        cedula_url = uploadRes.data.url;
      }
      
      await axios.post(`${API}/public/solicitud-reserva`, {
        propiedad_id: propiedad?.id_propiedad || id_propiedad,
        ...formReserva,
        cedula_url
      });
      
      setSolicitudEnviada(true);
      toast.success('¡Solicitud enviada! Nos contactaremos contigo pronto.');
    } catch (error) {
      console.error('Error al enviar solicitud:', error);
      toast.error('Error al enviar solicitud');
    } finally {
      setEnviando(false);
    }
  };

  const formatCurrency = (value) => {
    // Formatear con separador de miles con punto
    const formatted = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return formatted;
  };

  const handleDayClick = (date) => {
    // No permitir seleccionar días pasados
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      toast.error('No puedes seleccionar fechas pasadas');
      return;
    }

    // No permitir seleccionar días reservados
    if (isDayReserved(date)) {
      toast.error('Esta fecha no está disponible');
      return;
    }

    const dateStr = date.toISOString().split('T')[0];

    // Si no hay check-in, o si estamos reiniciando la selección
    if (!fechaCheckIn || (fechaCheckIn && fechaCheckOut)) {
      setFechaCheckIn(date);
      setFechaCheckOut(null);
      setSeleccionandoCheckOut(true);
      setFormReserva(prev => ({ ...prev, fecha_check_in: dateStr, fecha_check_out: '' }));
      toast.success('Check-in seleccionado. Ahora selecciona el check-out');
    } 
    // Si ya hay check-in, seleccionar check-out
    else if (fechaCheckIn && seleccionandoCheckOut) {
      if (date <= fechaCheckIn) {
        toast.error('El check-out debe ser después del check-in');
        return;
      }

      // Verificar que no haya días reservados en el rango
      let currentDate = new Date(fechaCheckIn);
      let hasReservedDays = false;
      while (currentDate <= date) {
        if (isDayReserved(currentDate)) {
          hasReservedDays = true;
          break;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (hasReservedDays) {
        toast.error('Hay días reservados en el rango seleccionado');
        return;
      }

      setFechaCheckOut(date);
      setSeleccionandoCheckOut(false);
      setFormReserva(prev => ({ ...prev, fecha_check_out: dateStr }));
      
      // Calcular precio
      calcularCotizacion(fechaCheckIn, date);
      
      toast.success('Fechas seleccionadas. ¡Completa el formulario para reservar!');
    }
  };

  const calcularCotizacion = async (checkIn, checkOut) => {
    try {
      const fechaInicio = checkIn.toISOString().split('T')[0];
      const fechaFin = checkOut.toISOString().split('T')[0];
      
      const targetId = propiedad?.id_propiedad || id_propiedad;
      const response = await axios.get(`${API}/public/propiedad/${targetId}/precios`, {
        params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin }
      });
      
      setCotizacion(response.data);
    } catch (error) {
      console.error('Error al calcular precio:', error);
    }
  };

  const limpiarSeleccion = () => {
    setFechaCheckIn(null);
    setFechaCheckOut(null);
    setSeleccionandoCheckOut(false);
    setCotizacion(null);
    setFormReserva(prev => ({ ...prev, fecha_check_in: '', fecha_check_out: '' }));
  };

  const isDayInRange = (date) => {
    if (!fechaCheckIn || !fechaCheckOut) return false;
    return date >= fechaCheckIn && date <= fechaCheckOut;
  };

  const isDaySelected = (date) => {
    if (!fechaCheckIn) return false;
    const dateStr = date.toISOString().split('T')[0];
    const checkInStr = fechaCheckIn.toISOString().split('T')[0];
    const checkOutStr = fechaCheckOut?.toISOString().split('T')[0];
    return dateStr === checkInStr || dateStr === checkOutStr;
  };


  const isDayReserved = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return disponibilidad.some(reserva => {
      // Incluir check-in y check-out como días ocupados
      const checkInStr = reserva.fecha_check_in;
      const checkOutStr = reserva.fecha_check_out;
      const checkIn = new Date(reserva.fecha_check_in + 'T00:00:00');
      const checkOut = new Date(reserva.fecha_check_out + 'T23:59:59');
      const currentDate = new Date(dateStr + 'T12:00:00');
      
      // El día está reservado si es el día de check-in, check-out, o cualquier día intermedio
      return dateStr === checkInStr || dateStr === checkOutStr || (currentDate >= checkIn && currentDate <= checkOut);
    });
  };

  const getDayStatus = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    for (const reserva of disponibilidad) {
      const checkInStr = reserva.fecha_check_in;
      const checkOutStr = reserva.fecha_check_out;
      const checkIn = new Date(reserva.fecha_check_in + 'T00:00:00');
      const checkOut = new Date(reserva.fecha_check_out + 'T23:59:59');
      const currentDate = new Date(dateStr + 'T12:00:00');
      
      // Verificar si el día está ocupado (check-in, check-out, o intermedio)
      if (dateStr === checkInStr || dateStr === checkOutStr || (currentDate >= checkIn && currentDate <= checkOut)) {
        return reserva.estado_reserva === 'Pendiente' ? 'pendiente' : 'reservado';
      }
    }
    return 'disponible';
  };

  const getPrecioDia = (date) => {
    if (!preciosInfo) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    const tipoArriendo = preciosInfo.tipo_arriendo || 'seasonal';
    
    // Si es mensual, mostrar precio diario proporcional
    if (tipoArriendo === 'monthly') {
      const precioMensual = preciosInfo.precio_mensual || 0;
      const precioDiario = precioMensual / 30;
      return { precio: precioDiario, tipo: 'mensual' };
    }
    
    // Arriendo seasonal
    const precioBase = preciosInfo.precio_base_noche;
    
    // Buscar precio personalizado por rango de fechas
    const precioPersonalizado = propiedad?.precios_personalizados?.find(p => {
      const inicio = new Date(`${p.fecha_inicio}T12:00:00`);
      const fin = new Date(`${p.fecha_fin}T12:00:00`);
      return date >= inicio && date <= fin;
    });
    if (precioPersonalizado) {
      return { precio: precioPersonalizado.precio_noche, tipo: 'personalizado' };
    }
    
    // Buscar descuento
    const descuento = propiedad?.descuentos?.find(d => {
      const inicio = new Date(`${d.fecha_inicio}T12:00:00`);
      const fin = new Date(`${d.fecha_fin}T12:00:00`);
      return date >= inicio && date <= fin;
    });
    
    if (descuento) {
      const precioConDescuento = precioBase * (1 - descuento.porcentaje / 100);
      return { precio: precioConDescuento, tipo: 'descuento', descuento: descuento };
    }
    
    return { precio: precioBase, tipo: 'base' };
  };

  const renderCalendar = (monthOffset = 0) => {
    const date = new Date(year, currentMonth + monthOffset, 1);
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    // Días vacíos al inicio
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }
    
    // Días del mes
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
      const dayStatus = getDayStatus(currentDate);
      const precioInfo = getPrecioDia(currentDate);
      const isPast = currentDate < today;
      const isToday = new Date().toDateString() === currentDate.toDateString();
      const isSelected = isDaySelected(currentDate);
      const isInRange = isDayInRange(currentDate);
      
      let bgColor = 'bg-green-50 text-green-700 hover:bg-green-100';
      let extraClasses = 'cursor-pointer';
      let isDisabled = false;
      
      // Destacar días con descuento
      if (precioInfo?.tipo === 'descuento' && dayStatus === 'disponible' && !isPast) {
        bgColor = 'bg-gradient-to-br from-yellow-100 to-yellow-200 text-yellow-900 hover:from-yellow-200 hover:to-yellow-300 ring-2 ring-yellow-400';
      }
      
      if (isPast) {
        bgColor = 'bg-gray-100 text-gray-400';
        extraClasses = 'cursor-not-allowed';
        isDisabled = true;
      } else if (dayStatus === 'reservado') {
        bgColor = 'bg-red-100 text-red-600 line-through';
        extraClasses = 'cursor-not-allowed';
        isDisabled = true;
      } else if (dayStatus === 'pendiente') {
        bgColor = 'bg-orange-100 text-orange-600';
        extraClasses = 'cursor-not-allowed';
        isDisabled = true;
      } else if (isSelected) {
        bgColor = 'bg-cyan-600 text-white font-bold ring-2 ring-cyan-400';
      } else if (isInRange) {
        bgColor = 'bg-cyan-100 text-cyan-800';
      }
      
      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDayClick(currentDate)}
          disabled={isDisabled}
          className={`h-14 flex flex-col items-center justify-center text-sm rounded ${bgColor} ${extraClasses} ${
            isToday ? 'ring-2 ring-cyan-500' : ''
          } transition-all relative`}
          title={
            precioInfo?.tipo === 'descuento' 
              ? `${precioInfo.descuento.nombre} - ${precioInfo.descuento.porcentaje}% OFF` 
              : dayStatus === 'pendiente' ? 'Fecha con solicitud pendiente' : ''
          }
        >
          <span className="font-semibold">{day}</span>
          {precioInfo?.tipo === 'descuento' && (
            <span className="text-xs font-bold text-yellow-900">-{precioInfo.descuento.porcentaje}%</span>
          )}
          {dayStatus === 'pendiente' && <span className="text-xs absolute top-0 right-0">⏳</span>}
        </button>
      );
    }
    
    return (
      <div className="mb-6">
        <h4 className="font-semibold text-lg mb-3 text-cyan-800">
          {monthNames[date.getMonth()]} {date.getFullYear()}
        </h4>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
            <div key={day} className="text-center text-xs font-semibold text-gray-600">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl text-cyan-700">Cargando...</div>
      </div>
    );
  }

  if (!propiedad) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-cyan-800 mb-4">Propiedad no encontrada</h1>
        </div>
      </div>
    );
  }

  const fotoFondoUrl = propiedad.fotos && propiedad.fotos.length > 0 
    ? `${BACKEND_URL}${propiedad.fotos[propiedad.foto_fondo_index || 0]}`
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50">
      <Toaster position="top-right" />
      
      {/* Header - Responsive */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-4 sm:py-6 shadow-lg">
        <div className="max-w-6xl mx-auto px-3 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Waves className="w-6 h-6 sm:w-8 sm:h-8" />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">{propiedad.nombre}</h1>
          </div>
          <div className="flex items-center gap-2 text-cyan-100 text-sm sm:text-base">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{propiedad.ubicacion}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Galería de Fotos */}
        {propiedad.fotos && propiedad.fotos.length > 0 && (
          <div className="mb-8">
            <div className="relative h-48 sm:h-64 md:h-80 lg:h-96 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl cursor-pointer group"
                 onClick={() => setModalGaleriaAbierto(true)}>
              <img
                src={`${BACKEND_URL}${propiedad.fotos[fotoActual]}`}
                alt={propiedad.nombre}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                <span className="text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  Ver galería ({propiedad.fotos.length} fotos)
                </span>
              </div>
              
              {propiedad.fotos.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFotoActual((prev) => (prev > 0 ? prev - 1 : propiedad.fotos.length - 1));
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                  >
                    <ChevronLeft className="w-6 h-6 text-cyan-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFotoActual((prev) => (prev < propiedad.fotos.length - 1 ? prev + 1 : 0));
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                  >
                    <ChevronRight className="w-6 h-6 text-cyan-700" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {propiedad.fotos.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-2 h-2 rounded-full ${idx === fotoActual ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Información Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Características Principales */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-cyan-800 mb-4">Características</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {propiedad.capacidad && (
                  <div className="flex items-center gap-3 bg-cyan-50 p-3 rounded-lg">
                    <Users className="w-6 h-6 text-cyan-600" />
                    <div>
                      <div className="text-2xl font-bold text-cyan-800">{propiedad.capacidad}</div>
                      <div className="text-xs text-gray-600">Personas</div>
                    </div>
                  </div>
                )}
                {propiedad.dormitorios && (
                  <div className="flex items-center gap-3 bg-cyan-50 p-3 rounded-lg">
                    <Bed className="w-6 h-6 text-cyan-600" />
                    <div>
                      <div className="text-2xl font-bold text-cyan-800">{propiedad.dormitorios}</div>
                      <div className="text-xs text-gray-600">Dormitorios</div>
                    </div>
                  </div>
                )}
                {propiedad.banos && (
                  <div className="flex items-center gap-3 bg-cyan-50 p-3 rounded-lg">
                    <Bath className="w-6 h-6 text-cyan-600" />
                    <div>
                      <div className="text-2xl font-bold text-cyan-800">{propiedad.banos}</div>
                      <div className="text-xs text-gray-600">Baños</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dormitorios */}
            {propiedad.dormitorios_descripciones && propiedad.dormitorios_descripciones.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-cyan-800 mb-4">Dormitorios</h2>
                <div className="space-y-3">
                  {propiedad.dormitorios_descripciones.map((dormitorio) => (
                    <div key={dormitorio.numero} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-bold">
                        {dormitorio.numero}
                      </div>
                      <p className="text-gray-700">{dormitorio.descripcion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Más Características */}
            {propiedad.caracteristicas && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-cyan-800 mb-4">Comodidades</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {propiedad.caracteristicas.split('\n').filter(c => c.trim()).map((caracteristica, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-gray-700">{caracteristica}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Condiciones de Pago */}
            {propiedad.condiciones_pago && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-cyan-800 mb-4">Condiciones de Pago</h2>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {propiedad.condiciones_pago}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Disponibilidad y Reserva */}
          <div className="space-y-6">
            {/* Disponibilidad */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-cyan-800 mb-4 flex items-center gap-2">
                <Calendar className="w-6 h-6" />
                Disponibilidad
              </h2>
              
              <div className="mb-4 flex justify-between items-center">
                <button
                  onClick={() => {
                    if (currentMonth > 0) {
                      setCurrentMonth(currentMonth - 1);
                    } else {
                      // Si estamos en enero, ir a diciembre del año anterior
                      setCurrentMonth(11);
                      setYear(year - 1);
                    }
                  }}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  title="Mes anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="font-semibold text-gray-800 text-center">
                  {new Date(year, currentMonth).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </div>
                
                <button
                  onClick={() => {
                    if (currentMonth < 11) {
                      setCurrentMonth(currentMonth + 1);
                    } else {
                      // Si estamos en diciembre, ir a enero del siguiente año (hasta 2030)
                      if (year < 2030) {
                        setCurrentMonth(0);
                        setYear(year + 1);
                      }
                    }
                  }}
                  className={`p-2 hover:bg-gray-100 rounded transition-colors ${year >= 2030 && currentMonth === 11 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={year >= 2030 && currentMonth === 11}
                  title={year >= 2030 && currentMonth === 11 ? 'Límite de calendario alcanzado (Diciembre 2030)' : 'Mes siguiente'}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {renderCalendar()}

              {/* Fechas Seleccionadas con Cotización */}
              {fechaCheckIn && (
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-300 rounded-xl p-4 mb-4 shadow-lg">
                  <div className="text-base font-bold text-cyan-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Fechas Seleccionadas
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Check-in:</span>
                      <span className="font-semibold text-cyan-800">
                        {fechaCheckIn.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                      </span>
                    </div>
                    {fechaCheckOut && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Check-out:</span>
                          <span className="font-semibold text-cyan-800">
                            {fechaCheckOut.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-cyan-200">
                          <span className="text-gray-700">
                            {cotizacion && cotizacion.tipo_arriendo === 'monthly' ? 'Días:' : 'Noches:'}
                          </span>
                          <div className="flex flex-col items-end">
                            <span className="font-bold text-cyan-900">
                              {Math.ceil((fechaCheckOut - fechaCheckIn) / (1000 * 60 * 60 * 24))}
                              {cotizacion && cotizacion.precio_promedio_noche > 0 && cotizacion.tipo_arriendo !== 'monthly' && (
                                <span className="text-sm font-normal text-gray-600 ml-1">
                                  (${formatCurrency(cotizacion.precio_promedio_noche)}/noche)
                                </span>
                              )}
                            </span>
                            {cotizacion && cotizacion.total > 0 && (
                              <span className="mt-1 px-2 py-1 bg-cyan-100 text-cyan-900 rounded-md font-bold text-sm">
                                Total: ${formatCurrency(cotizacion.total)}
                              </span>
                            )}
                          </div>
                        </div>

                        {cotizacion && (
                          <div className="mt-3 pt-3 border-t-2 border-cyan-300 space-y-2">
                            {/* Mostrar detalle de noches y total bruto */}
                            {cotizacion.tipo_arriendo !== 'monthly' && (
                              <div className="flex justify-between text-gray-700 text-sm">
                                <span>Valor {Math.ceil((fechaCheckOut - fechaCheckIn) / (1000 * 60 * 60 * 24))} noches (sin descuento):</span>
                                <span>${formatCurrency(cotizacion.subtotal_sin_descuento)}</span>
                              </div>
                            )}

                            {/* Mostrar detalle mensual si aplica */}
                            {cotizacion.tipo_arriendo === 'monthly' && cotizacion.precios_detalle && cotizacion.precios_detalle[0] && (
                              <>
                                {cotizacion.precios_detalle[0].meses_completos > 0 && (
                                  <div className="flex justify-between text-gray-700 text-sm">
                                    <span>{cotizacion.precios_detalle[0].meses_completos} mes(es) × ${formatCurrency(cotizacion.precios_detalle[0].precio_mensual)}:</span>
                                    <span>${formatCurrency(cotizacion.precios_detalle[0].total_meses)}</span>
                                  </div>
                                )}
                                {cotizacion.precios_detalle[0].dias_adicionales > 0 && (
                                  <div className="flex justify-between text-gray-700 text-sm">
                                    <span>{cotizacion.precios_detalle[0].dias_adicionales} día(s) adicional(es):</span>
                                    <span>${formatCurrency(cotizacion.precios_detalle[0].total_dias)}</span>
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Mostrar descuento aplicado y ahorro */}
                            {cotizacion.tipo_arriendo !== 'monthly' && cotizacion.monto_descuento > 0 && (
                              <div className="flex justify-between text-green-700 font-semibold bg-green-50 px-2 py-1 rounded text-sm">
                                <span className="flex items-center gap-1">
                                  ✨ Ahorro por descuento aplicado:
                                </span>
                                <span>-${formatCurrency(cotizacion.monto_descuento)}</span>
                              </div>
                            )}
                            
                            {/* Mostrar otros cobros (Ej: Aseo) */}
                            {cotizacion.otros_cobros && cotizacion.otros_cobros.length > 0 && (
                              <div className="pt-1">
                                {cotizacion.otros_cobros.map((cobro, idx) => (
                                  <div key={idx} className="flex justify-between text-gray-700 text-sm">
                                    <span>+ {cobro.nombre}:</span>
                                    <span>${formatCurrency(cobro.monto)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Monto Final a Pagar */}
                            <div className="flex justify-between pt-2 border-t-2 border-cyan-400 text-base mt-2">
                              <span className="font-bold text-cyan-900">Monto Final a Pagar:</span>
                              <span className="font-bold text-lg text-cyan-900">
                                ${formatCurrency(cotizacion.total)}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    onClick={limpiarSeleccion}
                    className="mt-3 text-sm text-red-600 hover:text-red-700 underline font-semibold"
                  >
                    Limpiar selección
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-50 border border-green-200 rounded"></div>
                  <span>Disponible</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-100 border border-red-200 rounded"></div>
                  <span>Reservado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gradient-to-br from-yellow-100 to-yellow-200 border border-yellow-400 rounded"></div>
                  <span>Con Descuento ✨</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-100 border border-orange-200 rounded"></div>
                  <span>Pendiente ⏳</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-cyan-600 border border-cyan-700 rounded"></div>
                  <span>Seleccionado</span>
                </div>
              </div>
              
              {/* Mostrar descuentos activos */}
              {preciosInfo && preciosInfo.descuentos && preciosInfo.descuentos.length > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4 rounded">
                  <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                    ✨ Promociones Activas:
                  </h4>
                  {preciosInfo.descuentos.map((desc, idx) => (
                    <div key={idx} className="text-xs text-yellow-800">
                      <span className="font-semibold">{desc.nombre}</span>: {desc.porcentaje}% de descuento
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={() => {
                  if (!fechaCheckIn || !fechaCheckOut) {
                    toast.error('Selecciona las fechas en el calendario');
                    return;
                  }
                  setModalReservaAbierto(true);
                }}
                className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white py-6 text-lg shadow-lg"
              >
                {fechaCheckIn && fechaCheckOut ? '¡Confirmar Reserva!' : 'Selecciona fechas en el calendario'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Galería */}
      {modalGaleriaAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <button
            onClick={() => setModalGaleriaAbierto(false)}
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 p-2 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="relative max-w-5xl w-full">
            <img
              src={`${BACKEND_URL}${propiedad.fotos[fotoActual]}`}
              alt={`Foto ${fotoActual + 1}`}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            
            {propiedad.fotos.length > 1 && (
              <>
                <button
                  onClick={() => setFotoActual((prev) => (prev > 0 ? prev - 1 : propiedad.fotos.length - 1))}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full"
                >
                  <ChevronLeft className="w-6 h-6 text-cyan-700" />
                </button>
                <button
                  onClick={() => setFotoActual((prev) => (prev < propiedad.fotos.length - 1 ? prev + 1 : 0))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full"
                >
                  <ChevronRight className="w-6 h-6 text-cyan-700" />
                </button>
              </>
            )}
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full">
              {fotoActual + 1} / {propiedad.fotos.length}
            </div>
          </div>
        </div>
      )}

      {/* Modal Reserva */}
      {modalReservaAbierto && !solicitudEnviada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Imagen de fondo */}
            {fotoFondoUrl && (
              <>
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${fotoFondoUrl})` }}
                />
                {/* Overlay blanco semi-transparente para legibilidad */}
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm" />
              </>
            )}
            
            {/* Sin foto de fondo - usar blanco sólido */}
            {!fotoFondoUrl && (
              <div className="absolute inset-0 bg-white" />
            )}
            
            {/* Contenido del formulario */}
            <div className="relative bg-transparent overflow-y-auto max-h-[90vh]">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-cyan-800">Solicitar Reserva</h2>
                  <button
                    onClick={() => setModalReservaAbierto(false)}
                    className="text-gray-400 hover:text-gray-600 bg-white/80 rounded-full p-1"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

              <form onSubmit={enviarSolicitud} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input
                      value={formReserva.nombre_cliente}
                      onChange={(e) => setFormReserva({...formReserva, nombre_cliente: e.target.value})}
                      placeholder="Ej: Juan"
                      required
                    />
                  </div>
                  <div>
                    <Label>Apellido *</Label>
                    <Input
                      value={formReserva.apellido_cliente}
                      onChange={(e) => setFormReserva({...formReserva, apellido_cliente: e.target.value})}
                      placeholder="Ej: González"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formReserva.email_cliente}
                      onChange={(e) => setFormReserva({...formReserva, email_cliente: e.target.value})}
                      placeholder="ejemplo@correo.com"
                      required
                    />
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input
                      type="tel"
                      value={formReserva.telefono_cliente}
                      onChange={(e) => setFormReserva({...formReserva, telefono_cliente: e.target.value})}
                      placeholder="+56 9 1234 5678"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>RUT / Documento *</Label>
                    <Input
                      value={formReserva.rut_cliente}
                      onChange={(e) => setFormReserva({...formReserva, rut_cliente: e.target.value})}
                      placeholder="Ej: 12.345.678-9"
                      required
                    />
                  </div>
                  <div>
                    <Label>Dirección *</Label>
                    <Input
                      value={formReserva.direccion_cliente}
                      onChange={(e) => setFormReserva({...formReserva, direccion_cliente: e.target.value})}
                      placeholder="Ej: Av. Providencia 1234"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Cédula o Documento de Identidad *</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setCedulaFile(e.target.files[0])}
                    required
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sube una foto o PDF de tu documento de identidad
                  </p>
                </div>

                <div>
                  <Label>Número de Personas *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={propiedad.capacidad || 10}
                    value={formReserva.num_personas}
                    onChange={(e) => setFormReserva({...formReserva, num_personas: e.target.value})}
                    placeholder="Ej: 4"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Check-in *</Label>
                    <Input
                      type="date"
                      value={formReserva.fecha_check_in}
                      onChange={(e) => setFormReserva({...formReserva, fecha_check_in: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div>
                    <Label>Check-out *</Label>
                    <Input
                      type="date"
                      value={formReserva.fecha_check_out}
                      onChange={(e) => setFormReserva({...formReserva, fecha_check_out: e.target.value})}
                      min={formReserva.fecha_check_in || new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Mensaje (opcional)</Label>
                  <textarea
                    value={formReserva.mensaje}
                    onChange={(e) => setFormReserva({...formReserva, mensaje: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="¿Tienes alguna pregunta o petición especial?"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={enviando}
                  className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 py-6 text-lg"
                >
                  {enviando ? (
                    'Enviando...'
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Enviar Solicitud
                    </span>
                  )}
                </Button>
              </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmación */}
      {solicitudEnviada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-cyan-800 mb-2">¡Solicitud Enviada!</h2>
            <p className="text-gray-600 mb-6">
              Gracias por tu interés. Nos pondremos en contacto contigo pronto para confirmar tu reserva.
            </p>
            <Button
              onClick={() => {
                setSolicitudEnviada(false);
                setModalReservaAbierto(false);
                setFormReserva({
                  nombre_cliente: '',
                  apellido_cliente: '',
                  email_cliente: '',
                  telefono_cliente: '',
                  fecha_check_in: '',
                  fecha_check_out: '',
                  num_personas: '',
                  mensaje: ''
                });
              }}
              className="bg-gradient-to-r from-cyan-600 to-cyan-700"
            >
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FanPage;
